import { faCalendarCirclePlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box } from '@unreserved-frontend-v2/ui/box/box'
import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { Maybe, OpenHouse } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { twMerge } from 'tailwind-merge'
import { format } from 'date-fns'
import useTranslation from 'next-translate/useTranslation'
import { AttendOpenHouseModal } from '../../attend-open-house/attend-open-house-modal'
import { useState } from 'react'
import { useRouter } from 'next/router'

interface OpenHouseScheduleProps {
  openHouses?: Maybe<OpenHouse[]>
  className?: string | undefined
  usePage?: boolean
}

export function OpenHouseSchedule({ openHouses, className, usePage }: OpenHouseScheduleProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [openHouseId, setOpenHouseId] = useState('')
  const { asPath } = useRouter()
  const handleAttendClick = (id: string) => {
    setOpenHouseId(id)
    setIsModalOpen(true)
  }
  const handleModalClose = () => setIsModalOpen(false)

  const { t } = useTranslation()
  const items = openHouses?.map((openHouse) => {
    // The slice removes the local timezone
    const startDate = new Date(openHouse.eventStartTime.slice(0, -1))
    const endDate = new Date(openHouse.eventEndTime.slice(0, -1))

    return {
      id: openHouse.id,
      weekday: { short: format(startDate, 'EEE').toUpperCase(), long: format(startDate, 'EEEE') },
      day: format(startDate, 'dd'),
      month: { short: format(startDate, 'MMM').toUpperCase(), long: format(startDate, 'MMMM') },
      year: format(startDate, 'yyyy'),
      time: `${format(startDate, 'h:mmaaaa')} to ${format(endDate, 'h:mmaaaa')}`,
    }
  })

  return items?.length ? (
    <Box className={twMerge('h-max w-full', className)} padding="p-0">
      <div className="border-shades-200 bg-shades-25 border-b px-4 py-3">
        <p className="text-lg font-bold">{t('listings:open-houses.schedule')}</p>
      </div>
      <div className="pb-4">
        <AttendOpenHouseModal openHouseId={openHouseId} isOpen={isModalOpen} onClose={handleModalClose} />
        {items?.map((item, i) => (
          <div className="space-y-4" key={i}>
            <div className="flex justify-between px-4 pt-4">
              <Box className="bg-shades-25 h-[79px] w-[68px] text-center">
                <p className="text-sm font-bold">{item.weekday.short}</p>
                <p className="text-xl font-extrabold">{item.day}</p>
                <p className="text-sm font-bold">{item.month.short}</p>
              </Box>
              <div className="flex-grow pl-3">
                <p className="text-lg font-bold">{item.weekday.long}</p>
                <p className="text-lg font-bold">
                  {item.month.long} {item.day}, {item.year}
                </p>
                <p className="pt-[3px]">{item.time}</p>
              </div>
              <FontAwesomeIcon icon={faCalendarCirclePlus} className="fa-lg text-primary top-0 right-0" />
            </div>
            <div className="px-4">
              {usePage ? (
                <Button
                  href={`/attend-open-house/${item.id}?redirectTo=${encodeURI(asPath)}`}
                  className="uppercase"
                  width="full"
                  variant="ghost"
                >
                  {t('listings:open-houses.attend')}
                </Button>
              ) : (
                <Button onClick={() => handleAttendClick(item.id)} className="uppercase" width="full" variant="ghost">
                  {t('listings:open-houses.attend')}
                </Button>
              )}
            </div>
            {items.length - 1 === i ? '' : <div className="border-shades-200 border-b"></div>}
          </div>
        ))}
      </div>
    </Box>
  ) : null
}
